/**
 * Delete an ámbito — a refusal wearing a dialog.
 *
 * WHY IT IS A MODAL AND NOT `window.confirm`
 * ------------------------------------------
 * The pages being replaced use `window.confirm` four times. It cannot
 * show WHO would lose access, and here that list is the decision: the
 * dialog opens on the impact dry run (`POST /{id}/impact` with
 * `rules: null` — the form the endpoint's own docstring names for
 * exactly this), so the analyst reads the groups and the count of people
 * before the button is live.
 *
 * WHAT THE DATABASE WOULD DO ANYWAY
 * ---------------------------------
 * `groups.scope_id` and `users.effective_scope_id` are both ON DELETE
 * RESTRICT (portal migration 053), deliberately, because every other
 * resolution silently WIDENS access. So the service asks first and
 * answers 409 naming them — there is no force flag, and this dialog
 * renders that refusal verbatim rather than turning it into "no se
 * pudo".
 *
 * The FILES go before the row and both go under the tenant write lock,
 * which is why this can be slow and why the confirm button says what is
 * about to happen rather than just "Eliminar".
 */

import { Button, Modal } from "@omnitok/ui";
import { Callout, StatusChip } from "@pervasivemind/admin-ui";
import { Spinner } from "@pervasivemind/bi-components";
import { useCallback, useEffect, useState } from "react";

import { useT } from "@/i18n";
import {
  scopesApi,
  type ScopeImpactOut,
  type ScopeSummaryOut,
} from "@/lib/access-api";

export function ScopeDeleteModal({
  scope,
  onClose,
  onDeleted,
}: {
  scope: ScopeSummaryOut | null;
  onClose: () => void;
  onDeleted: () => void;
}): JSX.Element | null {
  const { t } = useT();
  const [impact, setImpact] = useState<ScopeImpactOut | null>(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const scopeId = scope?.id ?? null;

  const load = useCallback(async (): Promise<void> => {
    if (scopeId === null) return;
    setLoading(true);
    setError(null);
    setImpact(null);
    try {
      setImpact(await scopesApi.impact(scopeId, null));
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [scopeId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (scope === null) return null;

  // The pre-check the service performs, mirrored HERE only to decide
  // whether the button is live. It is not a second copy of the rule:
  // the server refuses regardless, and its 409 is what the screen shows
  // if this ever disagrees.
  //
  // Known only once the dry run has answered. While it has not — still
  // loading, or it failed — the button stays off: "unknown" must not
  // read as "nothing blocks".
  const usage = impact === null ? null : impact.usage;
  const blocked =
    usage !== null && (usage.groups.length > 0 || usage.bound_user_count > 0);

  const submit = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      await scopesApi.remove(scope.id);
      onDeleted();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open
      onClose={() => (busy ? undefined : onClose())}
      title={t("access.scopes.delete.title", { name: scope.name })}
      size="md"
      footer={
        <div className="flex justify-end gap-2">
          <Button ghost size="sm" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button
            variant="danger"
            size="sm"
            disabled={loading || busy || impact === null || blocked}
            onClick={() => void submit()}
          >
            {t("access.scopes.delete.confirm")}
          </Button>
        </div>
      }
    >
      <div className="space-y-3">
        {loading && (
          <span className="inline-flex items-center gap-1.5 text-sm text-neutral-500">
            <Spinner size={14} ariaLabel={t("common.loading")} />
            {t("common.loading")}
          </span>
        )}

        {error !== null && (
          <Callout tone="danger" title={t("access.common.errorTitle")}>
            <span className="whitespace-pre-wrap">{error}</span>
          </Callout>
        )}

        {usage !== null && blocked && (
          <Callout tone="danger" title={t("access.scopes.delete.blockedTitle")}>
            <p>
              {t("access.scopes.delete.blocked", {
                groups: usage.groups.length,
                people: usage.bound_user_count,
              })}
            </p>
            <div className="mt-2 flex flex-wrap gap-1">
              {usage.groups.map((g) => (
                <StatusChip key={g.id} label={g.name} tone="warning" />
              ))}
            </div>
          </Callout>
        )}

        {!blocked && impact !== null && (
          <Callout tone="warning" title={t("access.scopes.delete.filesTitle")}>
            {t("access.scopes.delete.files")}
          </Callout>
        )}

        {impact !== null && (
          <p className="text-[13px] text-neutral-600">{impact.effective}</p>
        )}
      </div>
    </Modal>
  );
}
